/**
 * Banner Schedule Utility Functions
 * Handles validation and display logic for banner scheduling
 */

/**
 * Validates normal schedule configuration
 * @param {Object} banner - Banner object
 * @returns {Object} - { valid: boolean, error?: string }
 */
export function validateNormalSchedule(banner) {
    if (banner.scheduleType !== 'normal') {
        return { valid: true };
    }

    // If startDate is set, expiryDate must be set and after startDate
    if (banner.startDate !== null && banner.startDate !== undefined) {
        if (!banner.expiryDate) {
            return { valid: false, error: 'Expiry date is required when start date is set' };
        }
        if (banner.expiryDate <= banner.startDate) {
            return { valid: false, error: 'Expiry date must be after start date' };
        }
    }

    return { valid: true };
}

/**
 * Validates repeat schedule configuration
 * @param {Object} banner - Banner object
 * @returns {Object} - { valid: boolean, error?: string }
 */
export function validateRepeatSchedule(banner) {
    if (banner.scheduleType !== 'repeat') {
        return { valid: true };
    }

    if (!banner.repeatSchedule) {
        return { valid: false, error: 'Repeat schedule configuration is required' };
    }

    // At least one day must be selected
    if (!banner.repeatSchedule.days || banner.repeatSchedule.days.length === 0) {
        return { valid: false, error: 'At least one day must be selected for repeat schedule' };
    }

    // If not 24 hours, validate time range
    if (!banner.repeatSchedule.is24Hours) {
        const startTime = banner.repeatSchedule.startTime;
        const endTime = banner.repeatSchedule.endTime;

        if (startTime && endTime) {
            const [startHour, startMin] = startTime.split(':').map(Number);
            const [endHour, endMin] = endTime.split(':').map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            if (endMinutes <= startMinutes) {
                return { valid: false, error: 'End time must be after start time' };
            }
        }
    }

    return { valid: true };
}

/**
 * Validates banner schedule configuration
 * @param {Object} banner - Banner object
 * @returns {Object} - { valid: boolean, error?: string }
 */
export function validateBannerSchedule(banner) {
    if (!banner.scheduleType) {
        // Backward compatibility: banners without scheduleType are valid
        return { valid: true };
    }

    if (banner.scheduleType === 'normal') {
        return validateNormalSchedule(banner);
    } else if (banner.scheduleType === 'repeat') {
        return validateRepeatSchedule(banner);
    }

    return { valid: true };
}

/**
 * Checks if banner should be displayed based on normal schedule
 * @param {Object} banner - Banner object
 * @param {number} currentTimestamp - Current timestamp in milliseconds (default: Date.now())
 * @returns {boolean}
 */
export function checkNormalSchedule(banner, currentTimestamp = Date.now()) {
    const now = currentTimestamp;

    // If startDate is set, check if current time is after start
    if (banner.startDate && now < banner.startDate) {
        return false;
    }

    // If expiryDate is set, check if current time is before expiry
    if (banner.expiryDate && now > banner.expiryDate) {
        return false;
    }

    return true;
}

/**
 * Checks if banner should be displayed based on repeat schedule
 * @param {Object} banner - Banner object
 * @param {number} currentTimestamp - Current timestamp in milliseconds (default: Date.now())
 * @returns {boolean}
 */
export function checkRepeatSchedule(banner, currentTimestamp = Date.now()) {
    if (!banner.repeatSchedule) {
        return false;
    }

    const now = new Date(currentTimestamp);
    const currentDayName = now.toLocaleDateString('en-US', { weekday: 'long' }); // 'Monday', 'Tuesday', etc.

    // Check if current day is in the selected days
    if (!banner.repeatSchedule.days.includes(currentDayName)) {
        return false;
    }

    // If 24 hours, show banner
    if (banner.repeatSchedule.is24Hours) {
        return true;
    }

    // Check time window
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes

    const startTime = banner.repeatSchedule.startTime || '00:00';
    const endTime = banner.repeatSchedule.endTime || '23:59';

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    return currentTime >= startMinutes && currentTime <= endMinutes;
}

/**
 * Main function to check if banner should be displayed
 * @param {Object} banner - Banner object
 * @param {number} currentTimestamp - Current timestamp in milliseconds (default: Date.now())
 * @returns {boolean}
 */
export function shouldDisplayBanner(banner, currentTimestamp = Date.now()) {
    // Banner must be active
    if (!banner.active) {
        return false;
    }

    // If no schedule type, show banner (backward compatibility)
    if (!banner.scheduleType) {
        return true;
    }

    // Check based on schedule type
    if (banner.scheduleType === 'normal') {
        return checkNormalSchedule(banner, currentTimestamp);
    } else if (banner.scheduleType === 'repeat') {
        return checkRepeatSchedule(banner, currentTimestamp);
    }

    // Default: show if schedule type is unknown (backward compatibility)
    return true;
}
